import React, {ReactElement, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import AnimationForm from '../common/animationForm';
import {TABS} from '../../form/part/tabs';
import SlideChange from '../common/slideChange';
import Button from '../../../../incase-domain/components/button/Button';
import ToggleSwitch from '../../../../incase-domain/components/toggleSwitch/ToggleSwitch';
import {IElementHtml} from '../../../../incase-domain/elements/html';
import HtmlEditorModal from './HtmlEditorModal';
import {showHtmlCodeEditorModal} from '../../../../store/selectors/html.selectors';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import {isHtmlFileName} from '../../../../utils/htmlSlideImport';

interface HighlightedHtmlElementComponentProps {
    overrideHighlightedElement?: IElementHtml;
    overrideHighlightedElementChangeHandler?: {single: (field: keyof IElementHtml, value: unknown) => void};
    isFlipCard?: boolean;
    activeTab: string;
}

function HighlightedHtmlElementComponent(props: HighlightedHtmlElementComponentProps): ReactElement {
    const {overrideHighlightedElement, overrideHighlightedElementChangeHandler, isFlipCard = false, activeTab} = props;
    const openHtmlEditor = useSelector(showHtmlCodeEditorModal);
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementHtml;
    const element = overrideHighlightedElement || highlightedElement;

    const changeHandler = overrideHighlightedElementChangeHandler || updateHighlightedElement;

    const {t} = useTranslation();
    const htmlInputRef = useRef<HTMLInputElement>(null);

    const onImportHtml = (fileList: FileList | null): void => {
        const file = fileList?.[0];
        if (!file) return;

        if (!isHtmlFileName(file.name)) {
            toast.error(t('editorPage.invalidFile'));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const html = String(reader.result ?? '');
            if (!html.trim()) {
                toast.error(t('editorPage.emptyHtmlFile'));
                return;
            }
            changeHandler.single('text', html);
        };
        reader.readAsText(file);
    };

    const onToggleCoverSlide = (): void => {
        if (element.coverSlide) {
            updateHighlightedElement.single('coverSlide', false);
            return;
        }

        updateHighlightedElement.multiple(
            ['coverSlide', 'xPosition', 'yPosition', 'width', 'height', 'fitContent'],
            [true, 0, 0, '100%', '100%', false]
        );
    };

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                {!isFlipCard && (
                    <div className="l-group--form">
                        <div className="l-group__item">
                            <div className="u-mb-1">{t('designSidepanel.slideChangeOnClick')}</div>
                            <SlideChange
                                slideChange={element.slideChange}
                                setSlideChange={(slideChange) => {
                                    changeHandler.single('slideChange', slideChange);
                                }}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <div className="l-group__item">
                    {!isFlipCard && (
                        <div className="u-flex u-h-between u-v-center u-mb-1">
                            <div>{t('presEditPage.editSidebar.elements.coverSlide')}</div>
                            <ToggleSwitch
                                id={`html-cover-slide-${element.id}`}
                                isOn={Boolean(element.coverSlide)}
                                handleToggle={onToggleCoverSlide}
                            />
                        </div>
                    )}
                    <input
                        ref={htmlInputRef}
                        className="u-d-none"
                        type="file"
                        accept=".html,.htm"
                        onChange={(event) => {
                            const input = event.currentTarget;
                            onImportHtml(input.files);
                            input.value = '';
                        }}
                    />
                    <Button
                        variant="soft"
                        color="primary"
                        size="sm"
                        leading="solar:upload-minimalistic-linear"
                        onClick={() => htmlInputRef.current?.click()}
                    >
                        {t('editorPage.importHtml')}
                    </Button>
                </div>
            </div>

            <AnimationForm
                appearAfter={element.appearAfter || 0}
                elementAnimations={element.elementAnimations}
                highlightedElementChangeHandler={changeHandler}
            />

            {openHtmlEditor && <HtmlEditorModal element={element} highlightedElementChangeHandler={changeHandler} />}
        </>
    );
}

export default HighlightedHtmlElementComponent;
